/**
 * Cache dos blocos: o MESMO cache em dois níveis do satélite
 * (`goes/frameCache.ts`) — arquivos baixados e só uma janela decodificada —,
 * numa instância própria. Um bloco (~40–80 KB, 544 px) é decodificado em
 * poucos milissegundos pelo `createImageBitmap`, fora da thread principal.
 *
 * Os blocos nunca mudam (horário e largura estão no endereço, cache imutável):
 * o que sai da janela e volta é relido do cache HTTP do navegador, sem rede.
 */
import { FrameCache, type FrameLoader } from '../goes/frameCache';
import { fetchWithRetry, HttpError, sleep } from '../lib/fetchRetry';
import { registrarBloco } from './saude';
import { blocoDaUrl, registrarTamanho } from './tamanhos';
import { registrarVazao } from './vazao';

/** 503 (JPEG do STAR ainda incompleto no servidor): novas tentativas no próprio download. */
const TENTATIVAS_503 = 2;
const ESPERA_503_PADRAO_MS = 2000;
const ESPERA_503_MAX_MS = 15_000;

export const carregadorBloco: FrameLoader = {
  download: async (url, signal) => {
    for (let tentativa = 0; ; tentativa++) {
      const inicio = performance.now();
      try {
        const blob = await fetchWithRetry(
          url,
          { mode: 'cors', credentials: 'omit' },
          // Um corte sob demanda leva ~2 s (quase tudo download do STAR); 30 s de folga.
          { retries: 0, signal, timeoutMs: 30_000, read: (r) => r.blob() },
        );
        registrarBloco(null);
        registrarVazao(blob.size, inicio, performance.now());
        const b = blocoDaUrl(url);
        if (b) registrarTamanho(b.produto, b.nivel, blob.size);
        return blob;
      } catch (e) {
        registrarBloco(e);
        if (!(e instanceof HttpError && e.status === 503) || tentativa >= TENTATIVAS_503) throw e;
        // O servidor está no ar e diz quando voltar: espera (cancelável) e tenta de novo.
        await sleep(Math.min(ESPERA_503_MAX_MS, e.retryAfterMs ?? ESPERA_503_PADRAO_MS), signal);
      }
    }
  },
  decode: (blob) => createImageBitmap(blob),
};

/**
 * Instância única. Downloads: 12 em paralelo (arquivos pequenos, mesmo host
 * HTTP/2); decodificações: 6 (cada uma leva poucos ms).
 */
export const blocoCache = new FrameCache<number>(12, 6, carregadorBloco);
